import React, { type SubmitEvent } from 'react';

import { ArrowForwardIcon } from '@depop/web-ui-kit/Icons/ArrowForwardIcon';
import { ArrowLeftIcon } from '@depop/web-ui-kit/Icons/ArrowLeftIcon';
import { CancelIcon } from '@depop/web-ui-kit/Icons/CancelIcon';
import { LensIcon } from '@depop/web-ui-kit/Icons/LensIcon';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useClickAway } from 'react-use';
import { tabbable } from 'tabbable';

import { useSearchBarUtilities } from './SearchBar.hooks';
import { SearchBarInput } from './SearchBarInput';
import { SearchSuggestions } from './SearchSuggestions';
import { VisualSearchCameraButton } from './VisualSearchCameraButton';
import sharedStyles from './sharedStyles.module.css';

import {
  VARIANT_A_IDENTIFIER,
  VARIANT_B_IDENTIFIER,
  VARIANT_IDENTIFIER,
} from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { useUserSearch } from '@/modules/search/hooks/useUserSearch';

type Props = {
  isAutoFocused?: boolean;
  isPlaceholderAnimated?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  desktopOnly?: boolean;
  onFocus?: () => void;
  placeholder?: string;
  sendATEventOnFocus?: boolean;
  shouldShrink?: boolean;
  className?: string;
  handleOutsideClick?: () => void;
};

const SEARCH_SUGGESTIONS = [
  'pink ralph lauren shirt',
  'grey nike sweatpants',
  'black dickies cargos',
  'white linen trousers',
  'blue carhartt jeans',
  'black josie top',
  'red corteiz cargos',
  'white lululemon skirt',
  'black converse',
  'green corduroy shirt',
];

export function SearchBar({
  isAutoFocused,
  isPlaceholderAnimated,
  setIsOpen,
  desktopOnly,
  onFocus = () => null,
  placeholder,
  sendATEventOnFocus,
  shouldShrink,
  className,
  handleOutsideClick: handleOutsideClickOverride,
}: Props) {
  const {
    instanceId,
    inputValue,
    isFocused,
    menuIsOpen,
    searchSuggestions,
    recentSearchItems,
    rootRef,
    recentSuggestionsRef,
    clearSuggestionsRef,
    handleSubmit,
    onFocus: onSearchBarFocus,
    onBlur,
    setInput,
    setSuggestionAsSearch,
    clearRecentSearchHistory,
    handleClearButtonClick,
    isRefined,
    searchSuggestionsRef,
    handleOutsideClick,
    trendingSuggestionsRef,
    userSuggestionsRef,
    trendingSearchSuggestions,
  } = useSearchBarUtilities();

  const { web_6746_search_for_sellers } = useExperiments([
    'web_6746_search_for_sellers',
  ]);
  const isSearchSellersVariantA =
    web_6746_search_for_sellers === VARIANT_A_IDENTIFIER;
  const isSearchSellersVariantB =
    web_6746_search_for_sellers === VARIANT_B_IDENTIFIER;
  const isInExperiment = isSearchSellersVariantA || isSearchSellersVariantB;

  const { web_6599_search_bar_ds_rollout } = useExperiments([
    'web_6599_search_bar_ds_rollout',
  ]);
  const isDesignSystemVariant =
    web_6599_search_bar_ds_rollout === VARIANT_IDENTIFIER;

  const { web_8980_visual_search_fake_door } = useExperiments([
    'web_8980_visual_search_fake_door',
  ]);
  const isVisualSearchVariant =
    web_8980_visual_search_fake_door === VARIANT_IDENTIFIER;

  const { data: userSuggestions } = useUserSearch({
    enabled: isInExperiment && inputValue.trim().length > 0,
    query: inputValue,
  });

  const hasValue = Boolean(inputValue);
  const t = useTranslations('common');

  function handleSubmitSearch(e: SubmitEvent<HTMLFormElement>) {
    const tabbableElms = rootRef.current ? tabbable(rootRef.current) : [];
    tabbableElms[0]?.blur();
    handleSubmit(e);
  }

  function handleSearchBarFocus() {
    onSearchBarFocus(sendATEventOnFocus);
    onFocus();
    setIsOpen && setIsOpen(true);
  }

  function handleChevronClose() {
    setIsOpen && setIsOpen(false);
    const tabbableElms = rootRef.current ? tabbable(rootRef.current) : [];
    tabbableElms[0]?.blur();
  }

  useClickAway(rootRef, () => {
    handleOutsideClickOverride?.();
    handleOutsideClick();
  });

  const showBackIcon =
    !desktopOnly && isDesignSystemVariant && (menuIsOpen || isFocused);

  const showVisualSearch = isVisualSearchVariant && !hasValue;

  const expClass = isDesignSystemVariant
    ? sharedStyles['searchBarContainer--variant']
    : sharedStyles['searchBarContainer--control'];

  return (
    <div
      className={clsx(
        sharedStyles.searchBarContainer,
        expClass,
        sharedStyles['searchBarContainer--curved'],
        {
          [sharedStyles['searchBarContainer--desktopOnly']]: desktopOnly,
          [sharedStyles['searchBarContainer--visualSearch']]: showVisualSearch,
          [sharedStyles['searchBarContainer--hasValue']]: hasValue,
        },
        className
      )}
      ref={rootRef}
    >
      <form
        className={clsx(sharedStyles.wrapper, {
          [sharedStyles['wrapper--shouldShrink']]: shouldShrink,
        })}
        onSubmit={handleSubmitSearch}
        autoComplete="off"
        role="search"
      >
        {showBackIcon ? (
          <ArrowLeftIcon
            className={clsx(
              sharedStyles.searchBarIcon,
              sharedStyles['searchBarIcon--clickable'],
              {
                [sharedStyles['searchBarIcon--focused']]: isFocused || hasValue,
              }
            )}
            size={desktopOnly ? 20 : 24}
            title={t('Back')}
            role="button"
            tabIndex={0}
            onClick={handleChevronClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleChevronClose();
              }
            }}
          />
        ) : (
          <LensIcon
            className={clsx(sharedStyles.searchBarIcon, {
              [sharedStyles['searchBarIcon--focused']]: isFocused || hasValue,
            })}
            strokeWidth="1"
            width={desktopOnly ? 20 : 24}
            aria-hidden="true"
          />
        )}
        <SearchBarInput
          id={`searchBar__input${instanceId}`}
          placeholder={placeholder}
          onFocus={handleSearchBarFocus}
          onBlur={onBlur}
          value={inputValue}
          type="text"
          onChange={setInput}
          name="q"
          maxLength={2048}
          role="combobox"
          aria-label={t('SearchBar.Placeholder')}
          aria-expanded={menuIsOpen}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          {...(menuIsOpen ? { 'aria-controls': 'search-suggestions' } : {})}
          isSuggestionsVisible={menuIsOpen}
          isPlaceholderAnimated={isPlaceholderAnimated}
          suggestions={SEARCH_SUGGESTIONS}
          {...(isAutoFocused ? { autoFocus: true } : {})}
        />
        {showVisualSearch && (
          <VisualSearchCameraButton desktopOnly={desktopOnly} />
        )}
        {hasValue && (
          <div className={clsx(sharedStyles.searchBarButtons)}>
            {isDesignSystemVariant ? (
              <button
                className={clsx(sharedStyles.clearButtonIcon)}
                // eslint-disable-next-line no-restricted-syntax
                data-testid="searchBar__clear-btn"
                onClick={handleClearButtonClick}
                type="button"
                id={`searchBar__clear-btn${instanceId}`}
                aria-label={t('Clear')}
                title={t('Clear')}
              >
                <CancelIcon />
              </button>
            ) : (
              <button
                className={clsx(sharedStyles.clearButton)}
                // eslint-disable-next-line no-restricted-syntax
                data-testid="searchBar__clear-btn"
                onClick={handleClearButtonClick}
                type="button"
                id={`searchBar__clear-btn${instanceId}`}
              >
                {t('Clear')}
              </button>
            )}

            {!isDesignSystemVariant && (
              <button
                className={clsx(sharedStyles.submitButton)}
                // eslint-disable-next-line no-restricted-syntax
                data-testid="searchBar__submit-btn"
                id={`searchBar__submit-btn${instanceId}`}
                color="black"
                aria-label={t('Nav.Search')}
                title={t('Nav.Search')}
              >
                <ArrowForwardIcon strokeWidth="1" title="Search" />
              </button>
            )}
          </div>
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        {menuIsOpen && (
          <SearchSuggestions
            isVariant={isDesignSystemVariant}
            searchSuggestions={searchSuggestions}
            hasValue={hasValue}
            onClick={setSuggestionAsSearch}
            recentSearchItems={recentSearchItems}
            clearRecentSearchHistory={clearRecentSearchHistory}
            recentSuggestionsRef={recentSuggestionsRef}
            clearSuggestionsRef={clearSuggestionsRef}
            isRefined={isRefined}
            searchSuggestionsRef={searchSuggestionsRef}
            trendingSearchRef={trendingSuggestionsRef}
            userSuggestionsRef={userSuggestionsRef}
            trendingSearchSuggestions={trendingSearchSuggestions}
            userSuggestions={userSuggestions}
            showVisualSearchBanner={showVisualSearch && !desktopOnly}
          />
        )}
      </form>
    </div>
  );
}
